'use client';

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { Iconify } from 'src/components/iconify';
import StyledChipNonFade from 'src/components/styled-chip/styled-chip-non-fade';
import { NyttFärgTema } from 'src/theme/NyttFärgtema';

// ----------------------------------------------------------------------

export interface WelcomeBannerCTA {
    label: string;
    href?: string;
    onClick?: () => void;
    variant?: 'contained' | 'outlined';
    icon?: string;
}

interface WelcomeBannerProps {
    userName?: string;
    orgName?: string;
    orgType?: string;
    subtitle?: string;
    actions?: WelcomeBannerCTA[];
}

export default function WelcomeBanner({
    userName,
    orgName,
    orgType,
    subtitle,
    actions = [],
}: WelcomeBannerProps) {
    const primary = NyttFärgTema.PrimaryBase.hexkod;
    const secondary = NyttFärgTema.SecondaryBase.hexkod;

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                px: { xs: 3, md: 5 },
                py: { xs: 4, md: 5 },
                color: 'common.white',
                background: `linear-gradient(135deg, ${secondary} 0%, ${primary} 55%, ${alpha(primary, 0.85)} 100%)`,
                boxShadow: `0 18px 40px -18px ${alpha(secondary, 0.6)}`,
            }}
        >
            {/* Decorative orbs */}
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    top: -80,
                    right: -60,
                    width: 260,
                    height: 260,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha('#ffffff', 0.18)} 0%, transparent 65%)`,
                    pointerEvents: 'none',
                }}
            />
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    bottom: -70,
                    left: '35%',
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha('#5ee7df', 0.25)} 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ position: 'relative' }}
            >
                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                        {orgName && (
                            <Typography
                                variant="overline"
                                sx={{
                                    letterSpacing: '0.12em',
                                    opacity: 0.8,
                                    fontWeight: 600,
                                }}
                            >
                                {orgName}
                            </Typography>
                        )}
                        {orgType && (
                            <StyledChipNonFade label={orgType} color={alpha('#ffffff', 0.22)} />
                        )}
                    </Stack>

                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            mb: 1,
                            letterSpacing: '-0.02em',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        Välkommen tillbaka{userName ? `, ${userName}` : ''} 👋
                    </Typography>

                    {subtitle && (
                        <Typography variant="body1" sx={{ opacity: 0.78, maxWidth: 520 }}>
                            {subtitle}
                        </Typography>
                    )}

                    {actions.length > 0 && (
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            sx={{ mt: 3 }}
                        >
                            {actions.map((action, idx) => (
                                <Button
                                    key={idx}
                                    variant={action.variant ?? (idx === 0 ? 'contained' : 'outlined')}
                                    color="inherit"
                                    href={action.href}
                                    onClick={action.onClick}
                                    startIcon={action.icon ? <Iconify icon={action.icon} /> : undefined}
                                    sx={{
                                        fontWeight: 700,
                                        ...(action.variant === 'contained' || (idx === 0 && !action.variant)
                                            ? {
                                                  bgcolor: 'common.white',
                                                  color: secondary,
                                                  '&:hover': { bgcolor: alpha('#ffffff', 0.88) },
                                              }
                                            : {
                                                  borderColor: alpha('#ffffff', 0.5),
                                                  color: 'common.white',
                                                  '&:hover': {
                                                      borderColor: 'common.white',
                                                      bgcolor: alpha('#ffffff', 0.08),
                                                  },
                                              }),
                                    }}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}
